import React, { useMemo, useRef, useState } from 'react';
import EditorCuerpo from './EditorCuerpo';
import BotonesPlantilla from './BotonesPlantilla';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
} from '@claimup/shared/constants/expedient-documents';
import { TrashIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { avisosDePlantilla } from '@claimup/shared/helpers/email-template-warnings';
import { MAX_ESCALATION_ORDER, MAX_REPEATS_PER_TEMPLATE } from '@claimup/shared/constants/email-templates';

/**
 * Una plantilla de un cliente: su peldaño, su texto y cómo queda.
 *
 * El peldaño va **arriba y a la izquierda** porque es el dato que se consulta,
 * y el texto abajo porque es el que se edita. Lo que se mira mucho y se toca
 * poco no puede estar al final de una tarjeta larga.
 */

const PELDANOS = Array.from({ length: MAX_ESCALATION_ORDER }, (_, i) => i + 1);
const REPETICIONES = Array.from({ length: MAX_REPEATS_PER_TEMPLATE }, (_, i) => i + 1);

/**
 * Cuántos envíos de esta plantilla antes de subir de peldaño.
 *
 * En recobro esto se dice repitiendo el mismo id de SendGrid en un array de doce
 * posiciones. Aquí es un número, y se puede leer.
 */
export const Repeticiones = ({ plantilla, onCambiar, disabled }) => (
  <label className='flex items-center gap-2 text-xs text-slate-500'>
    <span>Se manda</span>
    <select
      aria-label={`Repeticiones de ${plantilla.description || plantilla.key}`}
      value={plantilla.repeats ?? 1}
      disabled={disabled}
      onChange={e => onCambiar(Number(e.target.value))}
      className='rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none disabled:bg-slate-50'
    >
      {REPETICIONES.map(n => (
        <option key={n} value={n}>
          {n === 1 ? '1 vez' : `${n} veces`}
        </option>
      ))}
    </select>
  </label>
);

/**
 * En qué punto de la escalera va esta plantilla.
 *
 * «— fuera» **no significa desactivada**: significa que existe para este cliente
 * pero solo se manda a mano. Es lo que quieres para un reenganche.
 */
export const Peldano = ({ plantilla, onCambiar, disabled }) => (
  <label className='flex items-center gap-2 text-xs text-slate-500'>
    <span>Contacto nº</span>
    <select
      aria-label={`Peldaño de ${plantilla.description || plantilla.key}`}
      value={plantilla.escalation_order ?? ''}
      disabled={disabled}
      onChange={e => onCambiar(e.target.value === '' ? null : Number(e.target.value))}
      className='rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none disabled:bg-slate-50'
    >
      <option value=''>— fuera</option>
      {PELDANOS.map(n => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  </label>
);

const TemplateCard = ({ plantilla, onGuardar, onCambiarPeldano, onCambiarRepeticiones, onQuitar, soloLectura, whatsapp, color }) => {
  const [asunto, setAsunto] = useState(plantilla.subject);
  const [guardando, setGuardando] = useState(false);
  const editorRef = useRef(null);

  /**
   * El cuerpo de partida.
   *
   * Una plantilla de antes del editor no tiene formato: se abre con su texto
   * como un solo trozo. Al guardarla pasa a tener formato, y ahí está la
   * migración — sin script y sin tocar las que nadie abre.
   */
  const opsIniciales = useMemo(() => {
    const cuerpo = plantilla.body_ops?.length
      ? [...plantilla.body_ops]
      : [{ insert: plantilla.body_text || '' }];

    // Las plantillas de antes guardaban los botones en una lista aparte y
    // salían al final del correo. Al abrirlas se meten al final del cuerpo,
    // que es donde salían: así se ven, se pueden mover, y al guardar la
    // lista vieja se vacía. La migración la hace abrir y guardar, sin script.
    (plantilla.buttons || []).forEach(b => cuerpo.push({ insert: { button: { ...b } } }));

    return cuerpo;
  }, [plantilla.body_ops, plantilla.body_text, plantilla.buttons]);

  const [cuerpoOps, setCuerpoOps] = useState(opsIniciales);
  const limpioRef = useRef(null);
  // Parte del `key` del editor: Quill no es controlado, asi que descartar los
  // cambios obliga a remontarlo. Sin esto, «Descartar» dejaba el estado bien y
  // el texto de la pantalla como estaba.
  const [reinicio, setReinicio] = useState(0);
  const [adjunto, setAdjunto] = useState(plantilla.attach_category || '');

  /**
   * Quill **normaliza** el contenido al montar: junta trozos y se asegura de
   * que acabe en salto de linea. Por eso lo que emite la primera vez no es
   * igual a lo que se le paso, y comparar contra `opsIniciales` daba sucio
   * siempre: el boton «Guardar» aparecia en TODAS las tarjetas sin que nadie
   * hubiera tocado nada. El estado limpio es su primera emision.
   */
  /**
   * Avisos sobre lo que hay escrito, calculados **mientras se escribe** y no al
   * guardar: quien se equivoca con una variable es quien teclea, y enterarse al
   * revisar un envío ya es tarde. No bloquean: se puede guardar con avisos.
   */
  const avisos = useMemo(
    () =>
      avisosDePlantilla({
        subject: asunto,
        body_text: (cuerpoOps || [])
          .map(op => (typeof op.insert === 'string' ? op.insert : ''))
          .join(''),
      }),
    [asunto, cuerpoOps]
  );

  const cambiarCuerpo = ops => {
    if (limpioRef.current === null) limpioRef.current = JSON.stringify(ops);
    setCuerpoOps(ops);
  };

  const sucio =
    asunto !== plantilla.subject ||
    adjunto !== (plantilla.attach_category || '') ||
    (limpioRef.current !== null && JSON.stringify(cuerpoOps) !== limpioRef.current);

  // La misma clave sale dos veces en pantalla cuando se abre el catálogo: la
  // copia del cliente y el texto maestro. Sin el prefijo, los dos campos
  // compartirían `id` y la etiqueta apuntaría al de arriba.
  const campo = sufijo => `${plantilla.company_id || 'std'}-${plantilla.key}-${sufijo}`;

  const guardar = async () => {
    setGuardando(true);
    try {
      // `buttons: []` vacía la lista vieja: sus botones ya viajan dentro del
      // cuerpo. Sin esto saldrían dos veces, los de dentro y los del final.
      // `attach_category` viaja SIEMPRE, incluso vacío: si solo se mandara
      // cuando hay valor, poner el desplegable en «Ninguno» no quitaría el
      // adjunto — se quedaría el de antes y la pantalla diría otra cosa.
      await onGuardar({
        subject: asunto,
        body_ops: cuerpoOps,
        buttons: [],
        attach_category: adjunto || null,
      });
    } finally {
      setGuardando(false);
    }
  };

  const descartar = () => {
    setAsunto(plantilla.subject);
    setCuerpoOps(opsIniciales);
    setAdjunto(plantilla.attach_category || '');
    limpioRef.current = null;
    setReinicio(n => n + 1);
  };

  return (
    <div
      className={`bg-white border rounded-lg p-4 ${
        plantilla.escalation_order === null ? 'border-slate-200' : 'border-blue-200'
      }`}
    >
      <div className='flex items-start justify-between gap-3 mb-3'>
        <div className='min-w-0'>
          <p className='font-medium text-slate-900'>{plantilla.description || plantilla.key}</p>
          <p className='text-xs font-mono text-slate-400'>{plantilla.key}</p>
        </div>
        <div className='flex items-center gap-3 shrink-0'>
          {onCambiarPeldano && <Peldano plantilla={plantilla} onCambiar={onCambiarPeldano} disabled={guardando} />}
          {onCambiarRepeticiones && plantilla.escalation_order !== null && (
            <Repeticiones plantilla={plantilla} onCambiar={onCambiarRepeticiones} disabled={guardando} />
          )}
          {onQuitar && (
            <button
              type='button'
              onClick={onQuitar}
              aria-label={`Quitar ${plantilla.description || plantilla.key}`}
              className='text-slate-400 hover:text-red-600'
            >
              <TrashIcon className='w-4 h-4' />
            </button>
          )}
        </div>
      </div>

      {plantilla.catalogue_drift && (
        <p className='flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mb-3'>
          <ArrowPathIcon className='w-3.5 h-3.5 shrink-0 mt-0.5' />
          El texto del catálogo ha cambiado desde que se copió. No se propaga solo: este cliente puede haberlo ajustado
          a su voz.
        </p>
      )}

      {plantilla.unknown_variables?.length > 0 && (
        <p className='text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 mb-3'>
          Pide variables que no sabemos rellenar: {plantilla.unknown_variables.join(', ')}
        </p>
      )}

      <label htmlFor={campo('asunto')} className='block text-xs font-medium text-slate-600 mb-1'>
        Asunto
      </label>
      <input
        id={campo('asunto')}
        value={asunto}
        readOnly={soloLectura}
        onChange={e => setAsunto(e.target.value)}
        className='w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm mb-3 focus:border-blue-400 focus:outline-none read-only:bg-slate-50'
      />

      <p className='block text-xs font-medium text-slate-600 mb-1'>Cuerpo</p>
      <EditorCuerpo
        // Remonta si cambia la plantilla que se está mirando: Quill guarda el
        // contenido por dentro y sin esto se quedaría con el de la anterior.
        key={`${campo('cuerpo')}-${reinicio}`}
        ref={editorRef}
        valorInicial={opsIniciales}
        onChange={cambiarCuerpo}
        soloLectura={soloLectura}
        // Para que el botón que se inserte se vea con el color de la empresa,
        // que es el que va a salir en el correo.
        color={color}
      />

      <div className='mt-3 flex flex-wrap items-center gap-3'>
        <BotonesPlantilla
          onInsertar={boton => editorRef.current?.insertarBoton(boton)}
          whatsapp={whatsapp}
          soloLectura={soloLectura}
        />
        {!soloLectura && (
          <button
            type='button'
            onClick={() => editorRef.current?.limpiarFormato()}
            className='text-xs text-slate-500 underline hover:text-slate-700'
          >
            Limpiar formato
          </button>
        )}
      </div>

      {avisos.length > 0 && (
        <ul className='mt-3 space-y-1.5'>
          {avisos.map(aviso => (
            <li
              key={aviso}
              className='flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5'
            >
              <ExclamationTriangleIcon className='w-3.5 h-3.5 shrink-0 mt-0.5' />
              <span>{aviso}</span>
            </li>
          ))}
        </ul>
      )}

      <label className='mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600'>
        Adjuntar un documento del expediente:
        <select
          value={adjunto}
          disabled={soloLectura || guardando}
          onChange={e => setAdjunto(e.target.value)}
          aria-label={`Adjunto de ${plantilla.description || plantilla.key}`}
          className='rounded-lg border border-slate-200 px-2 py-1 text-xs disabled:opacity-50'
        >
          <option value=''>Ninguno</option>
          {DOCUMENT_CATEGORIES.map(c => (
            <option key={c} value={c}>
              {DOCUMENT_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </label>

      {adjunto && (
        <p className='text-[11px] text-slate-400 mt-1'>
          Se manda el documento de ese tipo que esté marcado como <strong>enviable al deudor</strong>.
          Si el expediente no tiene ninguno, o tiene varios, <strong>ese correo no sale</strong> y la
          auditoría dice por qué: el texto promete un adjunto, y mandar el que no es sería peor.
        </p>
      )}

      <div className='mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3'>
        <p className='text-xs font-medium text-slate-500 mb-1'>Así queda</p>
        <p className='text-sm font-medium text-slate-800'>{plantilla.preview_subject}</p>
        <p className='text-sm text-slate-600 whitespace-pre-line mt-1'>{plantilla.preview_body}</p>
      </div>

      {sucio && !soloLectura && (
        <div className='flex items-center gap-2 mt-3'>
          <button
            type='button'
            onClick={guardar}
            disabled={guardando}
            className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium ${
              guardando ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type='button'
            onClick={descartar}
            disabled={guardando}
            className='text-xs text-slate-500 hover:text-slate-700'
          >
            Descartar
          </button>
          <span className='text-xs text-slate-400'>La vista previa se actualiza al guardar.</span>
        </div>
      )}
    </div>
  );
};

export default TemplateCard;
